import React, { useEffect, useState, useRef } from 'react';
import { Search, AlertTriangle, CheckCircle, Clock, Filter, CheckSquare } from 'lucide-react';
import { Instrument } from '../types/instruments';
interface InstrumentTableProps {
  instruments: Instrument[];
  onReviewComplete?: () => void;
  hasReviewed?: boolean;
}
export function InstrumentTable({
  instruments,
  onReviewComplete,
  hasReviewed
}: InstrumentTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'missing' | 'ok' | 'override'>('all');
  const tableRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const filteredInstruments = instruments.filter(inst => {
    const matchesSearch = inst.name.toLowerCase().includes(searchTerm.toLowerCase()) || inst.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || inst.status === filter;
    return matchesSearch && matchesFilter;
  });
  const missingCount = instruments.filter(i => i.status === 'missing').length;
  const okCount = instruments.filter(i => i.status === 'ok').length;
  const overrideCount = instruments.filter(i => i.status === 'override').length;
  const totalExpected = instruments.reduce((acc, curr) => acc + curr.expected, 0);
  const totalFound = instruments.reduce((acc, curr) => acc + curr.found, 0);
  const progress = totalExpected > 0 ? Math.round(totalFound / totalExpected * 100) : 0;
  // Detect scroll to bottom
  useEffect(() => {
    const handleScroll = () => {
      if (!tableRef.current) return;
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = tableRef.current;
      const isBottom = scrollTop + clientHeight >= scrollHeight - 10;
      if (isBottom && !hasScrolledToBottom) {
        setHasScrolledToBottom(true);
      }
    };
    const ref = tableRef.current;
    ref?.addEventListener('scroll', handleScroll);
    return () => ref?.removeEventListener('scroll', handleScroll);
  }, [hasScrolledToBottom]);
  const handleConfirmReview = () => {
    if (onReviewComplete) {
      onReviewComplete();
    }
  };
  // Auto-show missing filter when items are missing
  useEffect(() => {
    if (missingCount > 0 && filter === 'all') {
      setFilter('missing');
    }
  }, [missingCount]);
  return <div className="flex flex-col h-full bg-surface rounded-card border border-border overflow-hidden" data-id="element-139">
      {/* Header */}
      <div className="p-5 border-b border-border" data-id="element-140">
        <div className="flex items-center justify-between mb-4" data-id="element-141">
          <h2 className="text-xl font-bold text-text-primary" data-id="element-142">
            Instrument Checklist
          </h2>
          <span className="text-sm text-text-secondary bg-surface-2 px-3 py-1 rounded-input border border-border" data-id="element-143">
            {filteredInstruments.length} items
          </span>
        </div>

        {/* Search */}
        <div className="relative mb-4" data-id="element-144">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary" data-id="element-145" />
          <input type="text" placeholder="Search by name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full bg-surface-2 border border-border rounded-input pl-11 pr-4 py-3 text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-info focus:border-transparent" data-id="element-146" />
        </div>

        {/* Filter Chips */}
        <div className="flex items-center gap-2 flex-wrap" data-id="element-147">
          <Filter className="w-4 h-4 text-text-secondary" data-id="element-148" />
          <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-input text-sm font-medium transition-colors ${filter === 'all' ? 'bg-info text-white' : 'bg-surface-2 text-text-secondary border border-border hover:bg-surface'}`} data-id="element-149">
            All
          </button>
          {okCount > 0 && <button onClick={() => setFilter('ok')} className={`px-3 py-1.5 rounded-input text-sm font-medium transition-colors ${filter === 'ok' ? 'bg-success text-white' : 'bg-surface-2 text-text-secondary border border-border hover:bg-surface'}`} data-id="element-150">
              OK ({okCount})
            </button>}
          {missingCount > 0 && <button onClick={() => setFilter('missing')} className={`px-3 py-1.5 rounded-input text-sm font-medium transition-colors animate-pulse ${filter === 'missing' ? 'bg-critical text-white' : 'bg-surface-2 text-text-secondary border border-border hover:bg-surface'}`} data-id="element-151">
              Missing ({missingCount})
            </button>}
          {overrideCount > 0 && <button onClick={() => setFilter('override')} className={`px-3 py-1.5 rounded-input text-sm font-medium transition-colors ${filter === 'override' ? 'bg-warning text-white' : 'bg-surface-2 text-text-secondary border border-border hover:bg-surface'}`} data-id="element-152">
              Override ({overrideCount})
            </button>}
        </div>
      </div>

      {/* Table */}
      <div ref={tableRef} className="flex-1 overflow-y-auto" data-id="element-153">
        <table className="w-full" data-id="element-154">
          <thead className="bg-surface-2 sticky top-0 border-b border-border z-10" data-id="element-155">
            <tr data-id="element-156">
              <th className="text-left p-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-157">
                Instrument
              </th>
              <th className="text-left p-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-158">
                Slot
              </th>
              <th className="text-center p-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-159">
                Status
              </th>
              <th className="text-center p-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-160">
                Count
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border" data-id="element-161">
            {filteredInstruments.map(inst => <tr key={inst.id} className={`
                  transition-colors
                  ${inst.status === 'missing' ? 'bg-critical/5' : 'hover:bg-surface-2'}
                `} data-id="element-162">
                <td className="p-4" data-id="element-163">
                  <div data-id="element-164">
                    <div className="font-medium text-text-primary text-base" data-id="element-165">
                      {inst.name}
                    </div>
                    <div className="text-sm text-text-secondary" data-id="element-166">
                      {inst.category}
                    </div>
                  </div>
                </td>
                <td className="p-4" data-id="element-167">
                  <span className="inline-block font-mono text-sm text-text-primary bg-surface-2 px-3 py-1 rounded-input border border-border" data-id="element-168">
                    {inst.slot}
                  </span>
                </td>
                <td className="p-4 text-center" data-id="element-169">
                  {inst.status === 'ok' && <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-input text-sm font-medium bg-success/10 text-success border border-success/30" data-id="element-170">
                      <CheckCircle className="w-4 h-4" data-id="element-171" /> OK
                    </span>}
                  {inst.status === 'missing' && <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-input text-sm font-medium bg-critical/10 text-critical border border-critical/30" data-id="element-172">
                      <AlertTriangle className="w-4 h-4" data-id="element-173" /> Missing
                    </span>}
                  {inst.status === 'override' && <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-input text-sm font-medium bg-warning/10 text-warning border border-warning/30" data-id="element-174">
                      Override
                    </span>}
                  {inst.status === 'pending' && <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-input text-sm font-medium bg-surface-2 text-text-secondary border border-border" data-id="element-175">
                      <Clock className="w-4 h-4" data-id="element-176" /> Not Scanned
                    </span>}
                </td>
                <td className="p-4 text-center" data-id="element-177">
                  <div className="font-mono text-base" data-id="element-178">
                    <span className={inst.found < inst.expected ? 'text-critical font-bold' : 'text-text-primary'} data-id="element-179">
                      {inst.found}
                    </span>
                    <span className="text-text-secondary mx-1.5" data-id="element-180">/</span>
                    <span className="text-text-secondary" data-id="element-181">{inst.expected}</span>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Progress Footer */}
      <div className="p-5 bg-surface-2 border-t border-border" data-id="element-182">
        <div className="flex items-center justify-between mb-3" data-id="element-183">
          <span className="text-sm font-medium text-text-secondary" data-id="element-184">
            Reconciliation Progress
          </span>
          <span className="text-lg font-bold text-text-primary" data-id="element-185">
            {progress}%
          </span>
        </div>
        <div className="h-3 bg-background rounded-input overflow-hidden border border-border mb-4" data-id="element-186">
          <div className={`h-full transition-all duration-500 ${progress === 100 ? 'bg-success' : 'bg-info'}`} style={{
          width: `${progress}%`
        }} data-id="element-187" />
        </div>

        {/* Review Confirmation */}
        {progress > 0 && !hasReviewed && <button onClick={handleConfirmReview} disabled={!hasScrolledToBottom} className={`w-full h-12 rounded-input font-bold text-sm transition-all flex items-center justify-center gap-2 ${hasScrolledToBottom ? 'bg-info hover:bg-blue-500 text-white' : 'bg-surface-2 text-text-secondary border border-border cursor-not-allowed'}`} data-id="element-188">
            <CheckSquare className="w-4 h-4" data-id="element-189" />
            {hasScrolledToBottom ? 'Confirm Review Complete' : 'Scroll to bottom to confirm review'}
          </button>}

        {hasReviewed && <div className="flex items-center justify-center gap-2 text-success text-sm font-medium" data-id="element-190">
            <CheckCircle className="w-4 h-4" data-id="element-191" />
            Checklist reviewed and confirmed
          </div>}
      </div>
    </div>;
}